import { useTranslations } from 'next-intl'
import styled from 'styled-components'

import { ContentText, fontSizes, fontWeights, spacing } from '@fortum/elemental-ui'

import { usePriceToString } from '@/shared/hooks/usePriceToStringFormatter'
import { useTheme } from '@/shared/hooks/useTheme'

import type { PriceElementProps } from './types'
import { useContractEnergyPriceExtraText } from '../../PriceBreakdown/hooks/useContractEnergyPriceExtraText'
import { defineTheme, discountPercentToString, getEnergyPriceDetails } from '../utils'

export type EnergyPriceProps = PriceElementProps

export const EnergyPrice = ({
  tariffElement,
  contractTemplate,
  theme = 'light',
  priceSize = 'xl',
  renderLabel = true,
}: EnergyPriceProps) => {
  const tPurchaseFlow = useTranslations('purchaseFlow')
  const tAriaLabels = useTranslations('ariaLabels')
  const { colors } = useTheme()
  const { formatPrice, formatPriceWithUnit } = usePriceToString()
  const colorConfiguration = defineTheme(colors, theme)

  const {
    energyPrice,
    discountedEnergyPrice,
    formattedEnergyPrice,
    energyPriceToDisplay,
    duration,
    isEnergyDiscounted,
  } = getEnergyPriceDetails({
    tariffElement,
    contractTemplate,
    formatPriceWithUnit,
  })

  const energyPriceExtraText = useContractEnergyPriceExtraText(contractTemplate)
  const shouldShowSpotPriceExtraText = contractTemplate.priceType === 'SPOT' && renderLabel
  const energyPriceText =
    shouldShowSpotPriceExtraText && energyPriceExtraText
      ? `${energyPriceExtraText.text} ${energyPriceExtraText.sign} ${energyPriceToDisplay}`
      : energyPriceToDisplay

  return (
    <>
      {renderLabel && (
        <ContentText size="s" color={colorConfiguration.crossedPrice} mt={spacing.xxxs}>
          {tPurchaseFlow('electricityPrice')}
        </ContentText>
      )}
      <ContentText
        size={priceSize}
        fontWeight={fontWeights.medium}
        color={colorConfiguration.price}
        data-testid="contract-price"
      >
        {energyPriceText}

        {isEnergyDiscounted && (
          <>
            <CrossedOutText
              tag="span"
              display="flex"
              mt={spacing.xxxxs}
              aria-label={tAriaLabels('originalPrice')}
              color={colorConfiguration.crossedPrice}
            >
              {formattedEnergyPrice}
            </CrossedOutText>
            <ContentText
              tag="span"
              fontSize={fontSizes.s}
              color={colorConfiguration.duration}
              textWrap="word"
            >
              {tPurchaseFlow('discount', {
                discountDuration: duration?.amount ?? 'none',
                discountDurationType: duration?.type || 'MONTH',
                discountType: 'energy',
                discountPercent: discountPercentToString(
                  energyPrice,
                  discountedEnergyPrice,
                  formatPrice,
                ),
              })}
            </ContentText>
          </>
        )}

        {renderLabel && contractTemplate.priceType === 'HYBRID' && energyPriceExtraText && (
          <ContentText size="s" display="block" tag="span" color={colorConfiguration.price}>
            {energyPriceExtraText.sign} {energyPriceExtraText.text}
          </ContentText>
        )}
      </ContentText>
    </>
  )
}

const CrossedOutText = styled(ContentText)`
  text-decoration: line-through;
`
